import { colors } from "@apps/viewer/src/ts/config";
import React from "react";
import { FormattedMessage } from "react-intl";

import { BlockModel } from "@courses-models/course";
import { asyncTimeout } from "@use/async-timeout";
import { preloadImage } from "@use/preload-image";
import { hrtime } from "@use/universal-hrtime";

import { ThumbnailFade } from "@apps/viewer/src/ts/components/layout/Progressbar/Preview/transitions/ThumbnailFade";
import { Tooltip } from "@apps/viewer/src/ts/components/utils/Tooltip/Tooltip";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

/**
 * Specifies the structure of the previewTitle that subPreviews should adhere
 * to.
 */
export type PreviewTitle = {
  title: string;
  subTitle?: string;
};

export type PreviewProps<T extends BlockModel = BlockModel> = {
  /**
   * Specifies whether or not the preview is currently active
   */
  active: boolean;

  /**
   * Contains a reference to the bullet, which this preview should be pointing
   * towards.
   */
  targetRef: React.RefObject<HTMLLIElement | null>;

  /**
   * Specifies the step of the current preview, and how many steps there
   * exists in total
   */
  steps: {
    current: number;
    total: number;
  };

  /**
   * A reference to the model describing content of the block being previewed.
   */
  model: T;
};

export type PreviewState = {
  /**
   * Internally represents whether the preview should be open or not. We
   * use this internal state in order to be able to apply an exit delay, which
   * will ensure transitions won't activate if user is moving cursor from bullet
   * to preview itself.
   */
  isActive: boolean;

  /**
   * Will specify the absolute url to the thumbnail that should be displayed
   * within this preview.
   */
  thumbnailUrl?: string;

  /**
   * Will specify if the thumbnail should be faded into view once it has been
   * fully fetched.
   */
  fadeThumbnailIn?: boolean;
};

/**
 * Specifies in milliseconds how long we should wait in milliseconds before
 * hiding the preview after the mouse leaves the element.
 */
const PREVIEW_EXIT_DELAY = 100;

/**
 * Component responsible from render the Preview related to blocks displayed
 * in bullets in the progressBar
 */
export abstract class Preview<
  P extends PreviewProps,
  S extends PreviewState = PreviewState,
> extends React.Component<P, S> {
  /**
   * Contains a reference to a timeout created to add fade-in of the thumbnail
   * if it takes longer than 45ms to fetch it.
   */
  protected fadeThumbnailTimeout: number;

  /**
   * Contains a reference to the timeout that'll update internal state of preview
   * visibility to hidden after a specified delay
   */
  protected hideTimeout: number | undefined;

  /**
   * Contains the current data key, so we can safely determine if a new data
   * should be fetched from remote backends.
   */
  protected dataKey: string | undefined;

  /**
   * Fetch thumbnail instantly when the component is mounted into the DOM.
   */
  public componentDidMount(): void {
    if (this.props.active) {
      this.updateData().catch((err) => {
        /* Ignore thumbnail errors silently */
      });
    }
  }

  /**
   * Fetch updated thumbnail whenever the component has been updated, and
   * is active, if we need to do so.
   */
  public componentDidUpdate(): void {
    this.updatePreviewActiveState();

    if (this.props.active) {
      this.updateData().catch((err) => {
        /* Ignore thumbnail errors silently */
      });
    }
  }

  /**
   * Main render method
   */
  public render(): React.JSX.Element {
    return (
      <Tooltip
        fixed
        active={this.state.isActive}
        targetRef={this.props.targetRef}
        tooltipWidth={266}
      >
        {this.renderPreview()}
      </Tooltip>
    );
  }

  /**
   * Render that'll inject the image preview into the tooltip
   */
  protected renderPreview(): React.JSX.Element {
    return (
      <section>
        {this.renderProgress()}

        <div className="thumbnail-wrapper">
          {this.renderThumbnail()}
          {this.renderThumbnailFallback()}
        </div>

        {this.renderTitle()}

        <style jsx>{`
          section {
            /** Fixate dimensions of preview */
            width: 266px;
            font-size: 16px;
            line-height: 1em;

            cursor: pointer;
          }

          .thumbnail-wrapper {
            /** Allow usage of position: absolute within */
            position: relative;

            /** Force 16:9 aspect ratio for thumbnail */
            height: 150px;

            /** Ensure that thumbnailFallback icon cannot overflow */
            overflow: hidden;
          }
        `}</style>
      </section>
    );
  }

  /**
   * Helper that'll render the preview thumbnail (if one has been fetched).
   */
  protected renderThumbnail(): React.JSX.Element {
    // Extract state variables
    const { thumbnailUrl, fadeThumbnailIn } = (this.state ||
      {}) as PreviewState;

    const showThumbnail = !!thumbnailUrl;
    const fadeIn = fadeThumbnailIn === true;

    return (
      <ThumbnailFade in={showThumbnail} disabled={!fadeIn}>
        {(ref) => (
          <div ref={ref} className="thumbnail">
            <img src={thumbnailUrl} role="presentation" alt="" />

            <style jsx>{`
              .thumbnail {
                /** Overlap the entire preview area */
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                top: 0;
                z-index: 2;
              }

              img {
                /** Overlap the entire preview area */
                width: 100%;
                height: 100%;

                /** Properly scale the image */
                object-fit: cover;
                object-position: center;

                /** Ensure proper rendering for transparent PNGs */
                background: #f3f3f3;
              }
            `}</style>
          </div>
        )}
      </ThumbnailFade>
    );
  }

  /**
   * Helper that'll render the fallback for thumbnail, which is simply an icon
   * inside a grey placeholder.
   */
  protected renderThumbnailFallback(): React.JSX.Element {
    return (
      <div className="thumbnailFallback">
        <div className="icon">{this.renderThumbnailFallbackIcon()}</div>

        <style jsx>{`
          .thumbnailFallback {
            /** Overlap the entire preview area */
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            top: 0;
            z-index: 1;
          }

          .icon {
            /** Allow position: relative within */
            position: relative;

            /** Fill out container */
            width: 100%;
            height: 100%;

            /** Prevent thumbnail selection */
            pointer-events: none;
            user-select: none;

            /** Apply background color */
            background: #f3f3f3;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Implement a method that'll render a fallback for thumbnails that'll be
   * displayed in case an actual image cannot be fetched.
   */
  protected abstract renderThumbnailFallbackIcon(): React.JSX.Element;

  /**
   * Render that'll inject the progress indicator into the preview body
   */
  protected renderProgress(): React.JSX.Element {
    return (
      <div className="progress">
        <span className="progress__current">
          <FormattedMessage
            id="Progressbar__stepX__mobile"
            defaultMessage="Trin { x }"
            values={{
              x: this.props.steps.current,
            }}
          />
        </span>{" "}
        <FormattedMessage
          id="Progressbar__ofY__mobile"
          defaultMessage="af { y }"
          values={{
            y: this.props.steps.total,
          }}
        />
        <style jsx>{`
          .progress {
            /** Fixate height of the progress section */
            height: 30px;
            line-height: 30px;

            /** Setup internal padding */
            padding: 0 15px;

            /** Setup font */
            font-size: 14px;

            /** The current step should be highlighted */
            & .progress__current {
              color: ${colors.orange};
              font-weight: 700;
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Render that'll inject the title into the preview body
   */
  protected renderTitle(): React.JSX.Element {
    const titles = this.getTitle();

    const hasSubTitle = !!titles.subTitle;

    return (
      <div className={`${hasSubTitle ? "hasSubTitle" : "noSubTitle"}`}>
        <h4>{titles.title}</h4>
        {hasSubTitle && <h5>{titles.subTitle}</h5>}

        <style jsx>{`
          h4,
          h5 {
            /** Crop overflowing text */
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .hasSubTitle {
            & h4 {
              /** Position heading properly */
              margin: 10px 15px 2px;

              /** Setup font */
              font-weight: 400;
              color: ${colors.darkGrey};
            }

            & h5 {
              /** Position subheading properly */
              display: block;
              margin: 0 15px 10px;

              /** Setup font */
              font-size: 12px;
              font-weight: 400;
              color: ${colors.darkGrey};
            }
          }

          .noSubTitle {
            /** Setup the heading of the preview */
            & h4 {
              /** Override defaults and setup internal sizing */
              margin: 0;
              padding: 0 15px;

              /** Setup fixed height */
              height: 44px;
              line-height: 44px;

              /** Setup font */
              color: ${colors.darkGrey};
              font-size: 16px;
              font-weight: 400;
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Helper that'll fetch an updated thumbnail for this preview, so that it
   * can be faded into view once it becomes available.
   */
  protected async updateData(): Promise<void> {
    const dataKey = this.getDataKey();

    // Bail out if thumbnail hasn't changed
    if (this.dataKey === dataKey) {
      return;
    }

    this.dataKey = dataKey;

    // Begin fetching thumbnail and data...
    await Promise.all([this.fetchThumbnail(dataKey), this.fetchContent()]);
  }

  /**
   * Helper that'll fetch the thumbnail to display for this block, so that it
   * can be faded into view once available.
   */
  protected async fetchThumbnail(key: string | undefined): Promise<void> {
    // Register when we started fetching thumbnail
    const startedAt = hrtime();

    // If it takes longer than 45ms to fetch the thumbnail image, then we
    // should fade it into view
    window.clearTimeout(this.fadeThumbnailTimeout);

    this.fadeThumbnailTimeout = window.setTimeout(() => {
      this.setState({ fadeThumbnailIn: true });
    }, 45);

    try {
      // Get the url of the thumbnail that we need to display
      const thumbnailUrl = await this.getThumbnailUrl(key);

      // Preload the image before displaying it
      if (thumbnailUrl !== undefined) {
        await preloadImage(thumbnailUrl);
      }

      // Wait at least a minimum duration before fading the thumbnail into
      // view
      await asyncTimeout(Math.max(0, 1000 - (hrtime() - startedAt)));

      // ... And then make sure that it'll be displayed
      this.setState({ thumbnailUrl });
    } finally {
      // Prevent updating the fadeThumbnailIn state once the thumbnail has
      // actually been loaded
      clearTimeout(this.fadeThumbnailTimeout);
    }
  }

  /**
   * Implement a method that'll return the titles available for the given
   * preview.
   */
  protected abstract getTitle(): PreviewTitle;

  /**
   * Implement a method that'll return a key describing the content which the
   * current thumbnail was fetched for (so we know when to update it if the
   * component is being updated).
   */
  protected abstract getDataKey(): string | undefined;

  /**
   * Implement a method that'll fetch content required to render the given preview
   */
  protected abstract fetchContent(): Promise<void>;

  /**
   * Implement a method that'll resolve with an absolute url to an image that
   * should be displayed as a thumbnail in this block.
   */
  protected abstract getThumbnailUrl(
    key: string | undefined,
  ): Promise<string | undefined>;

  /**
   * Based on active prop, determine whether or not the preview should currently
   * be displayed, taking exit delay into account.
   */
  protected updatePreviewActiveState = () => {
    const { isActive } = this.state;

    // If we're currently activce, ensure timeout to hide preview is cleared
    if (this.props.active) {
      window.clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }

    if (this.props.active && !isActive) {
      // If we haven't internally updated our state to active, then do so now
      this.setState({ isActive: true });
    } else if (!this.props.active && isActive && !this.hideTimeout) {
      // ... else if the preview should be hidden, but the internal state
      // doesn't reflect this, and there haven't been created a timeout
      // to update internal state after a delay, then create it now!
      this.hideTimeout = window.setTimeout(() => {
        this.setState({ isActive: false });
        this.hideTimeout = undefined;
      }, PREVIEW_EXIT_DELAY);
    }
  };
}
